// Memberships & passes, the MEMBER side (MEMBERSHIPS_PLAN.md §7, D13, D14,
// D29, D31, D34, D35). Pure words and shapes only, so they are unit-tested
// without Vuetify; the network half is composables/useMembershipsMember.js.
// Copy mirrors the API's MembershipWording (never "members only").
import {formatMinor} from '~/utils/adminActionCopy';
import {DISPLAY_AS, FIELD_TYPE, membershipNoCharge, optionsFromJson} from '~/utils/memberships';
import {dateOnlyToLocal} from '~/utils/dateOnly';
import {nounFor} from './membershipWords.js';

export const money = (minor, currency) => formatMinor(minor ?? 0, currency);

/** "membership" / "pass" (D31). */
// X78 (R27 N3): the noun helpers live in membershipWords.js (the API's MembershipWording.WithNoun twin); re-exported for this module's importers.
export {nounFor, withNoun, withNouns} from './membershipWords.js';

/** "Get the membership" / "Get the pass" — the server sends joinVerb on eligibility; this is the fallback. Grant 2026-11-02: never "Join" for a membership. */
export const joinVerbFor = (displayAs) => displayAs === DISPLAY_AS.pass ? 'Get the pass' : 'Get the membership';

/** The relationship chips on Check and send (D34). "Other" opens a text box. */
export const RELATIONSHIPS = Object.freeze(['Mum', 'Dad', 'Other']);

/** The first name of "Jo Carter", for "Jo pays too" and "Send to Jo". */
export function firstNameOf(name) {
  const trimmed = String(name ?? '').trim();
  if (!trimmed) return '';
  return trimmed.split(/\s+/)[0];
}

/**
 * The steps of the apply flow (D34: a junior's application is 4 screens, an
 * adult's the same minus the parent block). "about" only exists when there is
 * something to ask. A group type (D42) adds "people" after the type, and asks
 * "about" only when the buyer answers for someone (answeredCount > 0).
 */
export function applySteps({questions = [], healthQuestions = [], group = false, answeredCount = null} = {}) {
  const steps = ['type'];
  if (group) steps.push('people');
  if ((questions.length || healthQuestions.length) && (!group || answeredCount == null || answeredCount > 0)) steps.push('about');
  steps.push('check', 'done');
  return steps;
}

/**
 * Why a type isn't available right now, as the player reads it, with where to
 * go about it (eligibility.reasonCode from the API).
 */
export function eligibilityNotice(eligibility, {noun = 'membership', clubId = null} = {}) {
  if (!eligibility || eligibility.eligible) return null;
  const code = eligibility.reasonCode;
  const base = {text: eligibility.reason || `You can't get this ${noun} right now.`, action: null, to: null, tone: 'info'};
  switch (code) {
    case 'noDateOfBirth':
      return {...base, action: 'Add your date of birth', to: {path: '/your-profile', query: {tab: 'basic'}}};
    case 'alreadyHolds':
      return {...base, text: eligibility.reason || `You already have this ${noun}.`, action: `See your ${noun}`, to: {path: '/your-profile', query: {tab: 'memberships'}}, tone: 'ok'};
    case 'pending':
      return {...base, text: eligibility.reason || 'Your application is with the club.', action: 'See your application', to: {path: '/your-profile', query: {tab: 'memberships'}}, tone: 'ok'};
    case 'full':
      return {...base, text: eligibility.reason || 'There are no places left.', tone: 'warn'};
    case 'age':
      return {...base, tone: 'warn'};
    case 'needsUpdate':
      return {...base, text: eligibility.reason || 'Update the app to get this one.', tone: 'warn'};
    case 'notNewToClub':
      // D44: an introductory type for people new to the club; the API's reason names the type to choose instead.
      return {...base, tone: 'warn', action: 'See the other memberships', to: clubId ? {path: `/clubs/${clubId}/memberships`} : null};
    case 'paymentsOff':
      // A paid type on a club whose payments are switched off: not available online (RETEST_R1 NM2).
      return {...base, text: eligibility.reason || "This isn't available online right now. Ask the club about it.", tone: 'warn'};
    default:
      return base;
  }
}

/**
 * The club's own questions as the prompt engine's schema (one page), so the
 * existing DynamicPromptForm renders them. Keys are the question ids; the
 * answers come back keyed the same and go to the API as [{questionId, value}].
 */
export function questionsAsPrompt(questions) {
  const typeName = (t) => {
    switch (t) {
      case FIELD_TYPE.date: return 'date';
      case FIELD_TYPE.dropdown: return 'dropdown';
      case FIELD_TYPE.checkbox: return 'checkbox';
      case FIELD_TYPE.numberPicker: return 'numberPicker';
      case FIELD_TYPE.infoBlock: return 'infoBlock';
      case FIELD_TYPE.consent: return 'consent';
      default: return 'textInput';
    }
  };
  const formElements = [...(questions ?? [])]
      .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
      .map((q) => {
        const inputType = typeName(q.fieldType);
        const el = {key: String(q.id), label: q.label, help: q.help ?? null, required: !!q.required, inputType};
        if (inputType === 'infoBlock') el.html = q.label;
        if (inputType === 'dropdown') {
          el.inputData = Object.fromEntries(optionsFromJson(q.optionsJson).map((o) => [o, o]));
        }
        if (inputType === 'textInput') el.maxLength = q.longAnswer ? 2000 : 250;
        return el;
      });
  return {id: 'membership-questions', pages: [{id: 'q', title: null, formElements}]};
}

/** promptAnswers() output → the API's answers list. */
export function answersForApi(answers) {
  return Object.entries(answers ?? {}).map(([questionId, value]) => ({questionId, value: value ?? null}));
}

// ── Health section (D24, D34) ──────────────────────────────────────────────

/** Empty answers for the health questions: ticks start false, everything else null. */
export function healthInitialValues(questions) {
  const values = {};
  for (const q of questions ?? []) {
    if (q.fieldType === FIELD_TYPE.infoBlock) continue;
    values[q.key] = q.fieldType === FIELD_TYPE.checkbox || q.fieldType === FIELD_TYPE.consent ? null : null;
    if (q.detailsPrompt) values[q.key + 'Details'] = '';
  }
  return values;
}

/** Errors keyed by question key: a required Yes/No needs one of the two; a Yes with a details prompt needs the details. */
export function healthValidate(questions, values) {
  const errors = {};
  for (const q of questions ?? []) {
    if (q.fieldType === FIELD_TYPE.infoBlock) continue;
    const v = values?.[q.key];
    if (q.fieldType === FIELD_TYPE.checkbox) {
      if (q.required && v !== true && v !== false) errors[q.key] = 'Please answer yes or no';
      else if (v === true && q.detailsPrompt && !String(values?.[q.key + 'Details'] ?? '').trim()) errors[q.key] = 'Please add the details';
    } else if (q.required && (v == null || String(v).trim() === '')) {
      errors[q.key] = 'Required';
    }
  }
  return errors;
}

/** The API's { key: value, keyDetails: text } dictionary (details only when the answer is Yes). */
export function healthAnswersForApi(questions, values) {
  const answers = {};
  for (const q of questions ?? []) {
    if (q.fieldType === FIELD_TYPE.infoBlock) continue;
    const v = values?.[q.key];
    if (q.fieldType === FIELD_TYPE.checkbox) {
      answers[q.key] = v === true;
      if (v === true && q.detailsPrompt) answers[q.key + 'Details'] = String(values?.[q.key + 'Details'] ?? '').trim();
    } else if (q.fieldType === FIELD_TYPE.numberPicker) {
      answers[q.key] = v == null || v === '' ? null : Number(v);
    } else {
      answers[q.key] = v == null || String(v).trim() === '' ? null : String(v).trim();
    }
  }
  return answers;
}

/** The stored answers (JSON elements from the API) back into the form's values. */
export function healthValuesFromAnswers(questions, answers) {
  const values = healthInitialValues(questions);
  for (const q of questions ?? []) {
    if (q.fieldType === FIELD_TYPE.infoBlock) continue;
    if (answers && q.key in answers) values[q.key] = answers[q.key];
    if (q.detailsPrompt && answers && (q.key + 'Details') in answers) values[q.key + 'Details'] = answers[q.key + 'Details'] ?? '';
  }
  return values;
}

/** "9 more questions · Health questions: active sports, v2" — the fold line under the first two. */
export function healthMoreLine(total, shown) {
  const more = Math.max(0, (total ?? 0) - (shown ?? 0));
  return more > 0 ? `${more} more question${more === 1 ? '' : 's'}` : '';
}

// ── Check and send (D34) ───────────────────────────────────────────────────

/**
 * The price box: what is paid now and what follows. Uses the eligibility's
 * priceNow + basis when the API worked out a late-join price, else the option.
 */
export function pricingBasisWords(basis) {
  switch (String(basis ?? '').toLowerCase()) {
    case '': case 'full': return null;
    case 'proratamonths': return 'Pro-rata for the months left';
    case 'proratasessions': return 'Pro-rata for the sessions left';
    case 'latejoin': return 'Late-join price';
    default: return String(basis);
  }
}

/**
 * The API's period ends are exclusive (the next term's first day): the day before is what people read,
 * the last day it's valid (the API's own "Ends 21 Oct" lines use the same day).
 *
 * The API sends a day as UTC midnight ("2027-10-22T00:00:00Z"). Taking a second off that instant and
 * reading it in local time gave 00:59 on the 22nd in British Summer Time, so the dialogs said "until
 * 22 October" where the chip said "Ends 21 Oct" (R15 N6). A UTC-midnight end is read as its calendar
 * day, and the day before is returned as local midnight; anything else keeps the one-second rule.
 */
export function periodEndDay(dateLike) {
  if (!dateLike) return null;
  const d = dateLike instanceof Date ? dateLike : new Date(dateLike);
  if (Number.isNaN(d.getTime())) return null;
  const bareDay = typeof dateLike === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateLike.trim());
  if (bareDay || (d.getUTCHours() === 0 && d.getUTCMinutes() === 0 && d.getUTCSeconds() === 0 && d.getUTCMilliseconds() === 0)) {
    return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() - 1);
  }
  return new Date(d.getTime() - 1000);
}

/**
 * The last day something is valid, for "until" / "Ends" words (R15 N6): the API's own lastDay /
 * termLastDay (X25) when it sends one, read as a calendar day; else worked out from the exclusive end.
 */
export function lastValidDay(lastDay, exclusiveEnd) {
  if (lastDay) {
    const day = dateOnlyToLocal(lastDay);
    if (day) return day;
  }
  return periodEndDay(exclusiveEnd);
}

export function priceNowLine(eligibility, option, type) {
  const currency = type?.currency ?? 'gbp';
  const now = eligibility?.priceNowMinor ?? option?.amountMinor ?? 0;
  const joining = option?.joiningFeeMinor ?? 0;
  const total = now + joining;
  if (total <= 0) return {amount: 'Free', detail: null};
  let detail = pricingBasisWords(eligibility?.priceNowBasis) || option?.summary || null;
  if (joining > 0) detail = [detail, `${money(joining, currency)} joining fee`].filter(Boolean).join(' + ');
  return {amount: money(total, currency), detail};
}

/**
 * What happens with the money, as one sentence under the card (D8/D34/D35).
 * payer: 'me' | 'parent' | 'someone'.
 */
export function moneyNote({payer, requiresApproval, amountText, parentFirstName, payerEmail, isFree, noun = 'membership', needsParent = false}) {
  if (isFree) {
    return requiresApproval ? `Nothing to pay. The club looks at your application and lets you know.` : `Nothing to pay. Your ${noun} starts straight away.`;
  }
  const named = !!parentFirstName;
  const who = parentFirstName || 'they';
  const them = parentFirstName || 'them';
  const whose = named ? `${parentFirstName}'s` : 'their';
  switch (payer) {
    case 'parent':
      return requiresApproval
          ? `One email for ${them} to agree and pay. The club looks at every application, so ${whose} card is held for ${amountText} and only taken if the club approves.`
          : `One email for ${them} to agree and pay.`;
    case 'someone':
      return requiresApproval
          ? `We'll email ${payerEmail || 'them'} a link to pay. Their card is held for ${amountText} and only taken if the club approves you.`
          : `We'll email ${payerEmail || 'them'} a link to pay. Your ${noun} starts once they've paid.`;
    default:
      if (parentFirstName) {
        return requiresApproval
            ? `Nothing is taken until ${who} agrees and the club approves. Then your card is charged ${amountText}.`
            : `Nothing is taken until ${who} agrees. Then your card is charged ${amountText}.`;
      }
      if (needsParent) {
        return requiresApproval
            ? `Nothing is taken until your parent or carer agrees and the club approves. Then your card is charged ${amountText}.`
            : `Nothing is taken until your parent or carer agrees. Then your card is charged ${amountText}.`;
      }
      return requiresApproval
          ? `Your card is held for ${amountText} now and charged once the club approves you. Nothing is taken if they decline.`
          : `Your card is charged ${amountText} now.`;
  }
}

/** The one tick on Check and send (D34): terms + code of conduct. */
export const TERMS_TICK = 'I agree to the membership terms and the code of conduct';

/**
 * Grant 2026-11-02 (Consumer Contracts Regulations 2013, reg 36): whether the confirm step asks the buyer to tick
 * "Start my membership now": only when THEY pay here (not free, not a parent or someone else paying from their own
 * link) and the API says the term starts inside the 14-day cancellation period. The words come from the API
 * (eligibility.startNowWording) so the app, the website and the email say the same thing.
 */
export function startNowNeeded({eligibility, payingMyself = false, isFree = false} = {}) {
  return !isFree && !!payingMyself && !!eligibility?.startNowRequired && !!eligibility?.startNowWording;
}
export const START_NOW_NEXT = 'Tick to start it now.';

/** The main button's words. */
/**
 * Grant 2026-11-02 ("Check and send … sounds like you are posting a parcel when in fact you are confirming a recurring
 * transaction"): the last step says what is being confirmed. Paying now: "Confirm and pay"; the club approves first:
 * "Confirm your application"; someone else is asked (a parent or carer, the payer, the other family) or nothing is
 * paid: "Confirm your details". The app's checkHeading says the same.
 */
export function checkHeading({payer, requiresApproval, isFree, needsParent = false, linkFamily = false}) {
  if (needsParent || payer === 'someone' || linkFamily) return 'Confirm your details';
  if (requiresApproval) return 'Confirm your application';
  return isFree ? 'Confirm your details' : 'Confirm and pay';
}

export function sendButtonLabel({payer, parentFirstName, requiresApproval, isFree, amountText, joinVerb, needsParent = false}) {
  if (parentFirstName) return `Send to ${parentFirstName}`;
  if (needsParent) return 'Send to your parent or carer';
  if (payer === 'someone') return 'Send the link';
  // Grant 2026-11-02: never "Join" for a membership (the player is already in the club): the API's "Join" reads "Get the membership".
  if (isFree) return requiresApproval ? 'Send application' : (joinVerb && joinVerb !== 'Join' ? joinVerb : 'Get the membership');
  if (requiresApproval) return `Apply and hold ${amountText}`;
  return `Pay ${amountText}`;
}

/** What the apply response means for the Done screen. */
export function doneState(res) {
  const outcome = res?.outcome;
  switch (outcome) {
    case 'active':
    case 'paid':
      return {kind: 'active', title: res?.membership?.displayAs === DISPLAY_AS.pass ? "You've got the pass" : "You're in", icon: 'mdi-check-circle', tone: 'success'};
    case 'awaitingParent':
      return {kind: 'parent', title: 'Application sent', icon: 'mdi-email-fast-outline', tone: 'primary'};
    case 'awaitingPayer':
      return {kind: 'payer', title: 'Link sent', icon: 'mdi-email-fast-outline', tone: 'primary'};
    case 'awaitingLink':
      // D45: the child's family confirms the link by email first; nothing is charged until then.
      return {kind: 'link', title: 'Waiting for the family to confirm', icon: 'mdi-email-fast-outline', tone: 'primary'};
    case 'pendingApproval':
    case 'held':
      return {kind: 'club', title: 'Application sent', icon: 'mdi-clock-outline', tone: 'primary'};
    case 'requiresAction':
      return {kind: 'action', title: 'One more step', icon: 'mdi-shield-check-outline', tone: 'primary'};
    case 'failed':
      return {kind: 'failed', title: "Your card didn't go through", icon: 'mdi-alert-circle-outline', tone: 'error'};
    case 'needsPayment':
      return {kind: 'pay', title: 'Almost there', icon: 'mdi-credit-card-outline', tone: 'primary'};
    default:
      return {kind: 'unknown', title: 'Sent', icon: 'mdi-check-circle', tone: 'primary'};
  }
}

// ── Profile → Memberships & passes ─────────────────────────────────────────

/** The row's second line: "Riverside Squash · £240 a year" / "6 Jan to 24 Mar · renews 31 Mar". */
export function membershipRowLine(m) {
  // D42: "Carter family · 3 people" / "Part of the Carter family" sits beside the club.
  return [m?.clubName, m?.groupLine, m?.renewsAsLine || m?.renewsOrEndsLine].filter(Boolean).join(' · ');
}

/** Yours vs the ones you pay for (D14). */
export function splitMine(memberships, myUserId) {
  const me = String(myUserId ?? '').toLowerCase();
  const mine = [];
  const payFor = [];
  for (const m of memberships ?? []) {
    if (String(m.memberUserId ?? '').toLowerCase() === me) mine.push(m);
    else payFor.push(m);
  }
  return {mine, payFor};
}

/** "Your Senior renewal didn't go through" banner data for a past-due membership (AppRenewalFailed). */
export function renewalFailedLine(m, formatDate) {
  if (!m || m.status !== 'PastDue') return null;
  const next = m.nextRetryAt ? formatDate(m.nextRetryAt) : null;
  return {
    title: `Your ${m.membershipTypeName} renewal didn't go through`,
    sub: next ? `We'll try again on ${next}. It stays active until then.` : `It stays active while we try again.`,
  };
}

/** What a membership can do from its own page (member or payer). */
export function membershipActions(m, {isPayer = false, isMember = true} = {}) {
  if (!m) return {};
  const ended = ['Cancelled', 'Expired'].includes(m.status);
  const ongoing = !ended;
  // D42: someone following a group's lead row has no money or term of their own; the group's lead (and its buyer) does.
  const follower = isGroupFollower(m);
  // Grant 2026-11-04: the member and the person paying can both cancel (and resume): a parent or carer on a child's,
  // whoever took over the renewals, the buyer of a group. The API lets both; a guest payer has no account.
  const canCancel = isMember || isPayer;
  return {
    payNow: !!m.canPayNow,
    cancel: canCancel && ongoing && !follower && !m.cancelAtPeriodEnd && (m.memberCanCancel || m.inCoolingOff),
    cancelNow: canCancel && ongoing && !follower && !!m.inCoolingOff,
    resume: canCancel && ongoing && !follower && !!m.cancelAtPeriodEnd,
    autoRenew: isMember && ongoing && !follower && m.status !== 'AwaitingPayment',
    // R17 L3: a no-charge or free membership is never charged, so there's nothing to ask anyone to pay.
    // Grant 2026-11-02: and only when there is something for them to pay: what is owed now, or the renewals still to
    // come. A membership paid up to next year that won't renew has nothing to hand over.
    askSomeone: isMember && ongoing && !follower && !m.pendingPayerEmail && !membershipNoCharge(m)
        && String(m.priceOptionSummary ?? '').trim().toLowerCase() !== 'free'
        && (!!m.canPayNow || membershipRenewalAhead(m)),
    stopPaying: isPayer && ongoing,
    health: isMember && !!m.hasHealthResponse && !!m.medicalResponseId,
  };
}

/** D42: a row on a group that follows the lead's billing (not the lead, still on the group). */
export const isGroupFollower = (m) => !!m?.membershipGroupId && !m.isGroupLead && !m.leftGroupAt;

/**
 * Grant 2026-11-04: the cancel step's words for whoever is looking. The holder reads their own ("Cancel membership",
 * "You keep the membership until 2 November 2027"); the person paying reads the member's name ("Cancel Sam's
 * membership?", "Sam keeps the membership until 2 November 2027"), or "their" when the name isn't known. The confirm,
 * cooling-off and refund lines are the same for both. `until` is the last valid day, already formatted.
 */
export function cancelWords(m, {isPayer = false, until = ''} = {}) {
  const noun = nounFor(m?.displayAs);
  const end = until || 'the end of the term';
  if (!isPayer) {
    return {
      button: `Cancel ${noun}`,
      title: `Cancel ${noun}`,
      keepLine: `You keep the ${noun} until ${end} and it isn't renewed.`,
      keptToast: `Cancelled. You keep it until ${end}.`,
      resumedToast: `Resumed. Your ${noun} renews as before.`,
    };
  }
  const name = String(m?.memberFirstName ?? '').trim();
  const whose = name ? `${name}'s` : 'their';
  const who = name || 'They';
  const keep = name ? 'keeps' : 'keep';
  return {
    button: `Cancel ${whose} ${noun}`,
    title: `Cancel ${whose} ${noun}?`,
    keepLine: `${who} ${keep} the ${noun} until ${end} and it isn't renewed.`,
    keptToast: `Cancelled. ${who} ${keep} it until ${end}.`,
    resumedToast: name ? `Resumed. ${name}'s ${noun} renews as before.` : `Resumed. Their ${noun} renews as before.`,
  };
}

/**
 * Grant 2026-11-02: whether a renewal is still to be charged: ongoing, renews automatically and not cancelled at the
 * end of the term. A set-dates type never renews (the API keeps autoRenew off for it) and a repeating-blocks pass
 * renews into its next block, so autoRenew alone says whether a next term is coming.
 */
export function membershipRenewalAhead(m) {
  if (!m || ['Cancelled', 'Expired'].includes(m.status)) return false;
  return !!m.autoRenew && !m.cancelAtPeriodEnd;
}

/**
 * The words behind "Ask someone else to pay" (Grant 2026-11-02: on a membership paid up to next year the plain button
 * read as if something was owed now). Owed now (awaiting payment, a failed renewal): the button and dialog as they
 * were. Otherwise it is the renewals they'd take over: "Ask someone else to pay your renewals", with "Paid until
 * 2 November 2027. From then, they'll pay." under it, and the dialog says nothing is charged now. A pass says its next
 * block instead. `longDate` is the page's own date formatter (useLocalisedDates).
 */
export function askSomeoneWords(m, {longDate = (d) => String(d)} = {}) {
  const noun = nounFor(m?.displayAs);
  if (!m || m.canPayNow || !membershipRenewalAhead(m)) {
    return {
      label: 'Ask someone else to pay',
      sub: null,
      intro: `We'll email them a link to pay for your ${noun}. They don't need an account, and they can stop paying any time.`,
    };
  }
  const end = lastValidDay(m.lastDay, m.currentPeriodEnd);
  const until = end ? longDate(end) : null;
  const paidUntil = until ? `paid until ${until}` : 'paid up';
  const cap = paidUntil[0].toUpperCase() + paidUntil.slice(1);
  if (m.displayAs === DISPLAY_AS.pass) {
    return {
      label: 'Ask someone else to pay your renewals',
      sub: `This block is ${paidUntil}. From the next block, they'll pay.`,
      intro: `We'll email them a link to save a card for your pass. Nothing is charged now: this block is ${paidUntil}, and they pay from the next block. They don't need an account, and they can stop paying any time.`,
    };
  }
  return {
    label: 'Ask someone else to pay your renewals',
    sub: `${cap}. From then, they'll pay.`,
    intro: `We'll email them a link to save a card for your ${noun}. Nothing is charged now: it's ${paidUntil}, and they pay from the next renewal. They don't need an account, and they can stop paying any time.`,
  };
}

/** R7 NM2: an application waiting on someone else's payment can name a new payer (POST …/payer). */
export function canAskSomeoneElseToPay(a) {
  return !!a && a.status === 'Pending' && !a.sentToClubAt && (!!a.payerDeclinedAt || !!a.payerEmail);
}

/** Application rows on the profile: what they wait on. */
export function applicationWaitLine(a) {
  if (!a) return '';
  switch (a.status) {
    case 'AwaitingParent': {
      const name = a.parentConsent?.parentName ? firstNameOf(a.parentConsent.parentName) : 'your parent or carer';
      return `Waiting for ${name} to agree`;
    }
    case 'AwaitingLink':
      return `Waiting for ${a.linkedChildName || "the child"}'s family to confirm`;
    case 'Pending':
      if (a.payerEmail && !a.sentToClubAt) return `Waiting for ${a.payerEmail} to pay`;
      // R5 M2 / R7 NM2: the payer said no (or let the link lapse): name them, and say what to do.
      if (a.payerDeclinedAt && !a.sentToClubAt) return `${a.payerDeclinedName ? firstNameOf(a.payerDeclinedName) : 'Your parent or carer'} didn't pay · ask someone else, or withdraw`;
      if (a.holdStatus === 'held') return 'With the club · your card is held';
      return a.sentToClubAt ? 'With the club' : 'Almost there: pay to finish';
    case 'Approved': return 'Approved';
    case 'Declined': return 'Declined';
    case 'Withdrawn': return 'Withdrawn';
    case 'Expired': return 'Expired';
    default: return a.status ?? '';
  }
}

// ── Branded pages (BOOKINGS_PLAN.md B17, SE18, SE19) ───────────────────────

/**
 * A return link the page may show: https only (http only on localhost), and
 * its host must be the club's website host when one is known. With nothing
 * to check against there is no back link (SE19: never an open redirect).
 */
export function safeReturnUrl(raw, {websiteUrl = null, allowedHosts = []} = {}) {
  if (!raw || typeof raw !== 'string') return null;
  let url;
  try { url = new URL(raw); } catch { return null; }
  const localhost = url.hostname === 'localhost' || url.hostname === '127.0.0.1';
  if (url.protocol !== 'https:' && !(url.protocol === 'http:' && localhost)) return null;
  const hosts = new Set((allowedHosts ?? []).map((h) => String(h).toLowerCase()));
  if (websiteUrl) {
    try { hosts.add(new URL(websiteUrl).hostname.toLowerCase()); } catch { /* not a url */ }
  }
  const host = url.hostname.toLowerCase();
  const allowed = [...hosts].some((h) => host === h || host.endsWith('.' + h));
  if (!allowed) return null;
  return {href: url.toString(), host: url.hostname};
}

/** The club's accent colour on a branded page, else ours. Only a 6-digit hex is trusted. */
export function brandAccent(brandColour, fallback = null) {
  const v = String(brandColour ?? '').trim();
  return /^#[0-9a-fA-F]{6}$/.test(v) ? v : fallback;
}

/** Initials for a club with no logo. */
export function clubInitials(name) {
  return String(name ?? '').split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join('');
}

/** A six-digit code field: digits only, max 6. */
export function cleanCode(text) {
  return String(text ?? '').replace(/\D/g, '').slice(0, 6);
}

/** A dumb email check for the parent / payer boxes; the API has the real rules (SE14). */
export function looksLikeEmail(text) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(text ?? '').trim());
}
